import {
  type LucideIcon,
  Zap, User, Car, Smartphone, Check, Smile, GraduationCap, Fuel, FileText,
  ClipboardList, Navigation, Leaf, Clock, PenLine, BookOpen, CalendarDays,
  Cake, Gift, SprayCan, ThumbsUp, ListChecks, Container, IdCard, Lightbulb,
  Newspaper, Baby, Camera, File, Type, Link, Folder, Trophy, Timer, Key, Circle,
  PartyPopper,
} from 'lucide-react'
import i18n from '@/i18n'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import type { ActionModuleResponse } from '@/api/model'

/**
 * For action modules without an uploaded image the backend provides an `iconName`
 * (lowercase, sometimes with spaces). We map it to a lucide icon; unknown/missing
 * names fall back to `Zap`.
 */
export const ICON_BY_NAME: Record<string, LucideIcon> = {
  person: User,
  carwash: Car,
  smartphone: Smartphone,
  checkmark: Check,
  celebration: PartyPopper,
  smile: Smile,
  training: GraduationCap,
  school: GraduationCap,
  'fuel station': Fuel,
  document: FileText,
  clipboard: ClipboardList,
  gps: Navigation,
  leaf: Leaf,
  eco: Leaf,
  clock: Clock,
  'stylus note': PenLine,
  'quick reference': BookOpen,
  'calendar month': CalendarDays,
  cake: Cake,
  'rewarded ads': Gift,
  mop: SprayCan,
  'thumb up': ThumbsUp,
  checklist: ListChecks,
  container: Container,
  'id card': IdCard,
  'emoji objects': Lightbulb,
  news: Newspaper,
  'escalator warning': Baby,
  'minor crash': Car,
  'photo camera': Camera,
  file: File,
  text: Type,
  url: Link,
  folder: Folder,
  leaderboard: Trophy,
  timetracker: Timer,
  key: Key,
  circle: Circle,
}

function iconForName(iconName: string | null | undefined): LucideIcon {
  if (!iconName) return Zap
  return ICON_BY_NAME[iconName.trim().toLowerCase()] ?? Zap
}

/**
 * Small inline icon (no border/tooltip) for lists like the groups dialog:
 * uploaded image (iconUrl), otherwise the lucide icon mapped from iconName.
 * Counterpart to `ShopModuleTypeIcon`.
 */
export function ActionModuleTypeIcon({
  module,
  className = 'size-4 text-muted-foreground',
}: {
  module: { iconUrl?: string | null; iconName?: string | null; colorHex?: string | null }
  className?: string
}) {
  if (module.iconUrl) {
    return <img src={module.iconUrl} alt="" className={`${className} object-contain`} />
  }
  const FallbackIcon = iconForName(module.iconName)
  return <FallbackIcon className={className} style={module.colorHex ? { color: module.colorHex } : undefined} />
}

/**
 * Action module in the table: uploaded image (iconUrl), otherwise the lucide icon
 * mapped from iconName, otherwise the fallback icon. Tooltip shows the module name.
 */
export function ActionModuleIcon({ module }: { module: ActionModuleResponse }) {
  const name = module.name ?? i18n.t('dialog.actionModules.fallbackName', { ns: 'groups' })
  const FallbackIcon = iconForName(module.iconName)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex size-8 items-center justify-center overflow-hidden rounded-md border bg-muted/40 text-foreground">
          {module.iconUrl ? (
            <img src={module.iconUrl} alt="" className="size-full object-contain" />
          ) : (
            <FallbackIcon className="size-4" style={module.colorHex ? { color: module.colorHex } : undefined} />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  )
}
